import React, { useState, useEffect, useRef } from "react";
import { useToast } from "../../contexts/ToastContext";
import api from "../../utils/api/axiosInstance";
import useDebounce from "../../hooks/useDebounce";
import getImageUrl from "../../utils/imageUtils";
import capitalizeFirst from "../../utils/capitalizeFirst";
import { MAX_IMAGE_UPLOAD_BYTES } from "../../constants/uploadLimits";
import MentionDropdown from "../common/MentionDropdown";
import { useMentionSearch } from "../../hooks/useMentionSearch";
import { collectMentionIdsNeedingResolution } from "../../utils/renderMentionText";import {
  FaTimes,
  FaImage,
  FaSearch,
  FaTag,
  FaGlobe,
  FaUserFriends,
  FaLock,
} from "react-icons/fa";
import styles from "../../styles/components/profile/EditPost.module.css";

const EditPost = ({ post, onPostUpdated, onCancel }) => {
  const { showToast } = useToast();
  // Form state - pre-populated with existing post data
  const [existingProductImages, setExistingProductImages] = useState({});
  const [newProductImages, setNewProductImages] = useState({});
  const [newProductImagePreviews, setNewProductImagePreviews] = useState({});
  const [products, setProducts] = useState([]);
  const [productSearch, setProductSearch] = useState("");
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [caption, setCaption] = useState("");
  const [tags, setTags] = useState("");
  const [location, setLocation] = useState("");
  const [visibility, setVisibility] = useState("public");
  const [postType, setPostType] = useState("standard");
  const [comparisonOptionAProductId, setComparisonOptionAProductId] =
    useState("");
  const [comparisonOptionBProductId, setComparisonOptionBProductId] =
    useState("");
  const [comparisonOptionATitle, setComparisonOptionATitle] = useState("");
  const [comparisonOptionBTitle, setComparisonOptionBTitle] = useState("");

  // Loading states
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [loadingPost, setLoadingPost] = useState(true);

  const captionInputRef = useRef(null);
  const {
    suggestions: captionMentionSuggestions,
    mentionQuery: captionMentionQuery,
    insertMention: insertCaptionMention,
    handleInput: handleCaptionMentionInput,
    clearMention: clearCaptionMention,
    serializeForSubmit: serializeCaptionMentions,
    seedRegistryFromStored,
  } = useMentionSearch(caption, setCaption, captionInputRef);

  const debouncedSearch = useDebounce(productSearch, 300);  const requiredProductCount = postType === "comparison" || postType === "battle" ? 2 : 1;
  const maxProducts = postType === "battle" ? 0 : requiredProductCount;

  const getProductId = (product) => {
    if (!product) return null;
    if (typeof product === "string") return product;
    return product._id || product.id || null;
  };

  const getTotalImageCount = (existingMap, newMap) => {
    const existingCount = Object.values(existingMap).reduce(
      (sum, list) => sum + list.length,
      0,
    );
    const newCount = Object.values(newMap).reduce(
      (sum, list) => sum + list.length,
      0,
    );
    return existingCount + newCount;
  };

  const getProductNameById = (productId) => {
    const product = products.find((p) => getProductId(p) === productId);
    return product ? capitalizeFirst(product.name || "") : "";
  };

  // Load post data when post changes
  useEffect(() => {
    if (post) {
      loadPostData();
    }
  }, [post]);

  const loadPostData = async () => {
    try {
      setLoadingPost(true);
      setNewProductImages({});
      setNewProductImagePreviews({});
      // Fetch full post data with populated products
      const res = await api.get(`/posts/${post._id}`);
      const postData = res.data;

      // Set products - ensure they are populated objects, not just IDs
      if (postData.products && postData.products.length > 0) {
        // Check if products are populated or just IDs
        const populatedProducts = postData.products.map((p) => {
          // If product is just an ID string, we need to fetch it
          if (typeof p === "string") {
            // Return the ID for now - we'll need to fetch full product data
            return { _id: p, name: "Loading...", images: [] };
          }
          // Product is already populated
          return p;
        });
        setProducts(populatedProducts);

        // If any products are just IDs, fetch their full data
        const idsToFetch = postData.products
          .filter((p) => typeof p === "string")
          .map((p) => p);

        if (idsToFetch.length > 0) {
          fetchProductDetails(idsToFetch);
        }
      }

      // Set existing product images
      if (
        postData.productImages &&
        Object.keys(postData.productImages).length > 0
      ) {
        setExistingProductImages(postData.productImages);
      } else if (postData.images && postData.images.length > 0) {
        const fallbackMap = {};
        const productIds = (postData.products || []).map((p) =>
          typeof p === "string" ? p : p._id,
        );
        const images = postData.images || [];
        productIds.forEach((productId, index) => {
          fallbackMap[productId] = images[index] ? [images[index]] : [];
        });
        if (images.length > productIds.length && productIds[0]) {
          const extra = images.slice(productIds.length);
          fallbackMap[productIds[0]] = [
            ...(fallbackMap[productIds[0]] || []),
            ...extra,
          ];
        }
        setExistingProductImages(fallbackMap);
      } else {
        setExistingProductImages({});
      }

      // Set caption (truncate to 500 characters to match backend validation)
      const captionText = postData.caption || "";
      const truncatedCaption =
        captionText.length > 500 ? captionText.substring(0, 500) : captionText;

      const idsToResolve = collectMentionIdsNeedingResolution(truncatedCaption);
      let nameById = {};
      if (idsToResolve.length > 0) {
        try {
          const bulkRes = await api.post("/products/bulk", { ids: idsToResolve });
          (Array.isArray(bulkRes.data) ? bulkRes.data : []).forEach((product) => {
            if (product?._id && product?.name) {
              nameById[product._id] = product.name;
            }
          });
        } catch (bulkErr) {
          console.error("Error resolving mention names:", bulkErr);
        }
      }

      setCaption(seedRegistryFromStored(truncatedCaption, nameById));
      // Warn user if caption was truncated
      if (captionText.length > 500) {
        showToast({
          message:
            "Caption was truncated to 500 characters to match the maximum allowed length.",
          type: "warning",
        });
      }

      // Set tags
      if (postData.tags && postData.tags.length > 0) {
        setTags(postData.tags.join(", "));
      }

      // Set location
      setLocation(postData.location || "");

      // Set visibility
      setVisibility(postData.visibility || "public");
      setPostType(postData.postType || "standard");

      const comparison = postData.comparison || {};
      const optionA = comparison.optionA || {};
      const optionB = comparison.optionB || {};
      const optionAProductId =
        (typeof optionA.product === "object"
          ? optionA.product?._id
          : optionA.product) || "";
      const optionBProductId =
        (typeof optionB.product === "object"
          ? optionB.product?._id
          : optionB.product) || "";

      setComparisonOptionAProductId(optionAProductId);
      setComparisonOptionBProductId(optionBProductId);
      setComparisonOptionATitle(optionA.title || "");
      setComparisonOptionBTitle(optionB.title || "");
    } catch (err) {
      console.error("Error loading post data:", err);
      showToast({
        message: "Failed to load post data",
        type: "error",
      });
    } finally {
      setLoadingPost(false);
    }
  };

  // Fetch full product details for product IDs
  const fetchProductDetails = async (productIds) => {
    try {
      const productPromises = productIds.map((id) =>
        api
          .get(`/products/${id}`)
          .then((res) => res.data)
          .catch(() => null),
      );
      const fetchedProducts = await Promise.all(productPromises);
      const validProducts = fetchedProducts.filter((p) => p !== null);

      // Update products state with full product data
      setProducts((prevProducts) => {
        return prevProducts.map((p) => {
          const fetched = validProducts.find(
            (fp) => fp._id === p._id || fp._id === p,
          );
          return fetched || p;
        });
      });
    } catch (err) {
      console.error("Error fetching product details:", err);
    }
  };

  // Fetch brand name by ID and update product
  const fetchBrandName = async (brandId, productId) => {
    try {
      const res = await api.get(`/brands/${brandId}`);
      const brandName = res.data?.name || null;
      if (brandName) {
        setProducts((prev) =>
          prev.map((prod) => {
            const prodId = prod._id?.toString() || prod._id;
            const targetId = productId?.toString() || productId;
            return prodId === targetId
              ? { ...prod, brand: { _id: brandId, name: brandName } }
              : prod;
          }),
        );
      }
      return brandName;
    } catch (err) {
      console.error("Error fetching brand:", err);
      return null;
    }
  };

  // Effect to fetch brand names for products with ObjectId brands
  useEffect(() => {
    if (products.length > 0) {
      products.forEach((product) => {
        if (
          product &&
          typeof product !== "string" &&
          typeof product.brand === "string" &&
          /^[0-9a-fA-F]{24}$/.test(product.brand) &&
          (!product.brand || typeof product.brand === "string") // Only if brand is still an ObjectId string
        ) {
          // Check if we already have brand name (avoid refetching)
          const hasBrandName =
            typeof product.brand === "object" && product.brand.name;
          if (!hasBrandName) {
            // Brand is an ObjectId - fetch the brand name
            fetchBrandName(product.brand, product._id);
          }
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products.length]); // Only run when products array length changes

  useEffect(() => {
    const productIds = products
      .map((product) => getProductId(product))
      .filter(Boolean);

    setExistingProductImages((prev) => {
      const next = {};
      productIds.forEach((id) => {
        next[id] = Array.isArray(prev[id]) ? prev[id] : [];
      });
      return next;
    });

    setNewProductImages((prev) => {
      const next = {};
      productIds.forEach((id) => {
        next[id] = Array.isArray(prev[id]) ? prev[id] : [];
      });
      return next;
    });

    setNewProductImagePreviews((prev) => {
      const next = {};
      productIds.forEach((id) => {
        next[id] = Array.isArray(prev[id]) ? prev[id] : [];
      });
      Object.keys(prev).forEach((id) => {
        if (!productIds.includes(id)) {
          (prev[id] || []).forEach((preview) => {
            if (preview && preview.startsWith("blob:")) {
              URL.revokeObjectURL(preview);
            }
          });
        }
      });
      return next;
    });
  }, [products]);

  useEffect(() => {
    if (postType !== "comparison" && postType !== "battle") {
      return;
    }

    const productIds = products.map((product) => getProductId(product)).filter(Boolean);

    setComparisonOptionAProductId((prev) => {
      if (prev && productIds.includes(prev)) return prev;
      return productIds[0] || "";
    });
    setComparisonOptionBProductId((prev) => {
      if (prev && productIds.includes(prev) && prev !== (productIds[0] || "")) {
        return prev;
      }
      return productIds.find((id) => id !== (productIds[0] || "")) || "";
    });
  }, [products, postType]);

  useEffect(() => {
    if (postType !== "comparison" && postType !== "battle") {
      return;
    }

    if (!comparisonOptionATitle && comparisonOptionAProductId) {
      setComparisonOptionATitle(getProductNameById(comparisonOptionAProductId));
    }
    if (!comparisonOptionBTitle && comparisonOptionBProductId) {
      setComparisonOptionBTitle(getProductNameById(comparisonOptionBProductId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    comparisonOptionAProductId,
    comparisonOptionBProductId,
    postType,
    products.length,
  ]);

  // Search products
  useEffect(() => {
    if (!debouncedSearch || debouncedSearch.length < 2) {
      setProductSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    const searchProducts = async () => {
      try {
        setSearching(true);
        const res = await api.get("/products/search", {
          params: { query: debouncedSearch, limit: 10 },
        });
        setProductSuggestions(Array.isArray(res.data) ? res.data : []);
        setShowSuggestions(true);
      } catch (err) {
        console.error("Error searching products:", err);
        setProductSuggestions([]);
      } finally {
        setSearching(false);
      }
    };

    searchProducts();
  }, [debouncedSearch]);

  // Handle image selection per product
  const handleImageSelect = (productId, e) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter((file) => {
      if (!file.type.startsWith("image/")) {
        showToast({
          message: `${file.name} is not an image file`,
          type: "error",
        });
        return false;
      }
      if (file.size > MAX_IMAGE_UPLOAD_BYTES) {
        showToast({
          message: `${file.name} is too large (max 5MB)`,
          type: "error",
        });
        return false;
      }
      return true;
    });

    const totalImages = getTotalImageCount(
      existingProductImages,
      newProductImages,
    );
    const availableSlots = 5 - totalImages;
    if (availableSlots <= 0) {
      showToast({
        message: "Maximum 5 images allowed",
        type: "error",
      });
      if (e.target) {
        e.target.value = "";
      }
      return;
    }

    const filesToAdd = validFiles.slice(0, availableSlots);
    if (filesToAdd.length < validFiles.length) {
      showToast({
        message: "Only 5 images allowed per post",
        type: "info",
      });
    }

    const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));
    setNewProductImages((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || []), ...filesToAdd],
    }));
    setNewProductImagePreviews((prev) => ({
      ...prev,
      [productId]: [...(prev[productId] || []), ...newPreviews],
    }));

    if (e.target) {
      e.target.value = "";
    }
  };

  // Remove existing image from a product
  const removeExistingImage = (productId, index) => {
    setExistingProductImages((prev) => ({
      ...prev,
      [productId]: (prev[productId] || []).filter((_, i) => i !== index),
    }));
  };

  // Remove new image from a product
  const removeNewImage = (productId, index) => {
    setNewProductImages((prev) => ({
      ...prev,
      [productId]: (prev[productId] || []).filter((_, i) => i !== index),
    }));
    setNewProductImagePreviews((prev) => {
      const previews = prev[productId] || [];
      const removedPreview = previews[index];
      if (removedPreview && removedPreview.startsWith("blob:")) {
        URL.revokeObjectURL(removedPreview);
      }
      return {
        ...prev,
        [productId]: previews.filter((_, i) => i !== index),
      };
    });
  };

  // Add product
  const addProduct = (product) => {
    if (products.length >= maxProducts) {
      showToast({
        message:
          postType === "comparison" || postType === "battle"
            ? `${postType === "battle" ? "Battle" : "Comparison"} posts can have up to 2 products`
            : "Standard posts allow only 1 product",
        type: "error",
      });
      return;
    }
    if (products.some((p) => p._id === product._id)) {
      showToast({
        message: "Product already added",
        type: "error",
      });
      return;
    }
    const productId = getProductId(product);
    setProducts([...products, product]);
    if (productId) {
      setExistingProductImages((prev) => ({
        ...prev,
        [productId]: prev[productId] || [],
      }));
      setNewProductImages((prev) => ({
        ...prev,
        [productId]: prev[productId] || [],
      }));
      setNewProductImagePreviews((prev) => ({
        ...prev,
        [productId]: prev[productId] || [],
      }));
    }
    setProductSearch("");
    setShowSuggestions(false);
  };

  // Remove product
  const removeProduct = (productId) => {
    const previews = newProductImagePreviews[productId] || [];
    previews.forEach((preview) => {
      if (preview && preview.startsWith("blob:")) {
        URL.revokeObjectURL(preview);
      }
    });
    setProducts(products.filter((p) => p._id !== productId));
    setExistingProductImages((prev) => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
    setNewProductImages((prev) => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
    setNewProductImagePreviews((prev) => {
      const { [productId]: _, ...rest } = prev;
      return rest;
    });
  };

  // Submit post update
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    const totalImages = getTotalImageCount(
      existingProductImages,
      newProductImages,
    );

    if (totalImages === 0) {
      showToast({
        message: "Please keep at least one image",
        type: "error",
      });
      return;
    }

    if (totalImages > 5) {
      showToast({
        message: "Maximum 5 images allowed",
        type: "error",
      });
      return;
    }

    if ((postType === "comparison" || postType === "battle") && products.length !== 2) {
      showToast({
        message: `${postType === "battle" ? "Battle" : "Comparison"} posts require exactly 2 products`,
        type: "error",
      });
      return;
    }

    const productIds = products
      .map((p) => {
        if (typeof p === "string") return p;
        return p._id || p.id;
      })
      .filter(Boolean);

    const missingImages = productIds.filter((productId) => {
      const existing = existingProductImages[productId] || [];
      const added = newProductImages[productId] || [];
      return existing.length + added.length === 0;
    });

    if (missingImages.length > 0) {
      showToast({
        message: "Please add at least one image for each product",
        type: "error",
      });
      return;
    }

    if (postType === "comparison" || postType === "battle") {
      if (productIds.length < 2) {
        showToast({
          message: `${postType === "battle" ? "Battle" : "Comparison"} posts require 2 products`,
          type: "error",
        });
        return;
      }
      if (!comparisonOptionAProductId || !comparisonOptionBProductId) {
        showToast({
          message: "Please select products for both options",
          type: "error",
        });
        return;
      }
      if (comparisonOptionAProductId === comparisonOptionBProductId) {
        showToast({
          message: "Option A and Option B must be different products",
          type: "error",
        });
        return;
      }
      if (!comparisonOptionATitle.trim() || !comparisonOptionBTitle.trim()) {
        showToast({
          message: "Please provide titles for both options",
          type: "error",
        });
        return;
      }
    }

    try {
      setLoading(true);

      // Prepare form data
      const formData = new FormData();

      // Extract product IDs
      const productImagesOrder = [];
      productIds.forEach((productId) => {
        const files = newProductImages[productId] || [];
        productImagesOrder.push({ productId, count: files.length });
        files.forEach((image) => {
          formData.append("images", image);
        });
      });

      formData.append("products", JSON.stringify(productIds));
      formData.append(
        "existingProductImages",
        JSON.stringify(existingProductImages),
      );
      formData.append("productImagesOrder", JSON.stringify(productImagesOrder));
      formData.append("postType", postType === "battle" ? "battle" : postType);
      formData.append("caption", serializeCaptionMentions(caption || ""));      formData.append("visibility", visibility || "public");

      if (postType === "comparison" || postType === "battle") {
        formData.append(
          "comparison",
          JSON.stringify({
            optionA: {
              title: comparisonOptionATitle.trim(),
              productId: comparisonOptionAProductId,
            },
            optionB: {
              title: comparisonOptionBTitle.trim(),
              productId: comparisonOptionBProductId,
            },
          }),
        );
      }

      if (tags) {
        const tagArray = tags
          .split(",")
          .map((t) => t.trim())
          .filter((t) => t.length > 0);
        formData.append("tags", JSON.stringify(tagArray));
      }

      if (location) {
        formData.append("location", location);
      }

      const res = await api.put(`/posts/${post._id}`, formData);

      showToast({
        message: "Post updated successfully!",
        type: "success",
      });

      onPostUpdated?.(res.data);
    } catch (err) {
      console.error("Error updating post:", err);
      showToast({
        message:
          err.response?.data?.message || err.message || "Failed to update post",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  // Cleanup previews on unmount
  useEffect(() => {
    return () => {
      Object.values(newProductImagePreviews).forEach((previews) => {
        previews.forEach((url) => {
          if (url && url.startsWith("blob:")) {
            URL.revokeObjectURL(url);
          }
        });
      });
    };
  }, [newProductImagePreviews]);

  if (loadingPost) {
    return (
      <div className={styles.editPost}>
        <div className={styles.loading}>Loading post data...</div>
      </div>
    );
  }

  const content = (
      <div className={styles.editPost}>
        <div className={styles.header}>
          <h2>
            Edit {postType === "battle" ? "Battle" : postType === "comparison" ? "Comparison" : "Product"} Post
          </h2>
        </div>

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Post Type</label>
            <p className={styles.helpText}>
              {(postType === "comparison" || postType === "battle")
                ? `${postType === "battle" ? "Battle" : "Comparison"} post (type cannot be changed after creation).`
                : "Standard post (type cannot be changed after creation)."}
            </p>
          </div>

          {/* Products (Required) */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>
              <FaTag /> Products (Required) *
            </label>
            <div className={styles.productSearchWrapper}>
              <div className={styles.searchInputWrapper}>
                <FaSearch className={styles.searchIcon} />
                <input
                  type="text"
                  placeholder="Search for products..."
                  value={productSearch}
                  onChange={(e) => {
                    setProductSearch(e.target.value);
                    setShowSuggestions(true);
                  }}
                  onFocus={() => setShowSuggestions(true)}
                  className={styles.searchInput}
                />
                {searching && (
                  <span className={styles.loading}>Searching...</span>
                )}
              </div>

              {showSuggestions && productSuggestions.length > 0 && (
                <div className={styles.suggestionsList}>
                  {productSuggestions.map((product) => (
                    <div
                      key={product._id}
                      className={styles.suggestionItem}
                      onClick={() => addProduct(product)}
                    >
                      {product.images && product.images.length > 0 && (
                        <img
                          src={getImageUrl(product.images[0])}
                          alt={product.name}
                          className={styles.suggestionImage}
                        />
                      )}
                      <div className={styles.suggestionInfo}>
                        <div className={styles.suggestionName}>
                          {capitalizeFirst(product.name)}
                        </div>
                        {product.brand && (
                          <div className={styles.suggestionBrand}>
                            {typeof product.brand === "object"
                              ? product.brand.name
                              : product.brand}
                          </div>
                        )}
                        {product.price && (
                          <div className={styles.suggestionPrice}>
                            ${product.price}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Selected Products */}
            {products.length > 0 && (
              <div className={styles.selectedProducts}>
                <h4>
                  Selected Products ({products.length}/
                  {maxProducts})
                </h4>
                {products.map((product) => {
                  // Handle case where product might be just an ID string
                  const productId =
                    typeof product === "string" ? product : product._id;
                  const productName =
                    typeof product === "string"
                      ? "Loading..."
                      : product.name || "Unknown Product";
                  // Get brand - handle all possible structures
                  let productBrand = null;
                  if (typeof product !== "string" && product.brand) {
                    productBrand = product.brand;
                  }
                  const productImages =
                    typeof product === "string" ? [] : product.images || [];

                  return (
                    <div key={productId} className={styles.selectedProductCard}>
                      <div className={styles.productInfo}>
                        {productImages.length > 0 && (
                          <img
                            src={getImageUrl(productImages[0])}
                            alt={productName}
                            className={styles.productImage}
                          />
                        )}
                        <div className={styles.productDetails}>
                          <div className={styles.productName}>
                            {productName}
                          </div>
                          {(() => {
                            // Helper to check if a string is an ObjectId (24 hex characters)
                            const isObjectId = (str) => {
                              return (
                                typeof str === "string" &&
                                /^[0-9a-fA-F]{24}$/.test(str)
                              );
                            };

                            // Get brand name safely - handle all possible brand structures
                            let brandName = null;
                            if (productBrand) {
                              if (typeof productBrand === "object") {
                                // Brand is an object - try to get name property
                                // Handle cases where brand might be { _id: "...", name: "..." } or just { name: "..." }
                                brandName = productBrand.name || null;
                              } else if (typeof productBrand === "string") {
                                // Brand is a string - check if it's an ObjectId
                                if (!isObjectId(productBrand)) {
                                  // It's a valid brand name string, not an ID
                                  brandName = productBrand;
                                }
                                // If it is an ObjectId, brandName stays null
                              }
                            }

                            // Only render if we have a valid brand name
                            return brandName ? (
                              <div className={styles.productBrand}>
                                {brandName}
                              </div>
                            ) : null;
                          })()}
                        </div>
                      </div>
                      <button
                        type="button"
                        className={styles.removeProductButton}
                        onClick={() => removeProduct(productId)}
                        aria-label="Remove selected product"
                      >
                        <FaTimes />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {products.length === 0 && postType !== "battle" && (
              <p className={styles.errorText}>
                {postType === "comparison"
                  ? "Exactly 2 products are required"
                  : "Exactly 1 product is required"}
              </p>
            )}
          </div>

          {(postType === "comparison" || postType === "battle") && (
            <div className={styles.section}>
              <label className={styles.sectionLabel}>
                {postType === "battle" ? "Battle" : "Comparison"} Setup *
              </label>
              <p className={styles.helpText}>
                Keep two different products and update option labels for voting.
              </p>
              <div className={styles.section}>
                <label className={styles.helpText}>Option A Product</label>
                <select
                  value={comparisonOptionAProductId}
                  onChange={(e) => setComparisonOptionAProductId(e.target.value)}
                  className={styles.searchInput}
                >
                  <option value="">Select product</option>
                  {products.map((product) => {
                    const id = getProductId(product);
                    const name =
                      typeof product === "string"
                        ? "Loading..."
                        : capitalizeFirst(product.name || "");
                    return (
                      <option key={`a-${id}`} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  value={comparisonOptionATitle}
                  onChange={(e) => setComparisonOptionATitle(e.target.value)}
                  placeholder="Option A title"
                  className={styles.searchInput}
                />
              </div>
              <div className={styles.section}>
                <label className={styles.helpText}>Option B Product</label>
                <select
                  value={comparisonOptionBProductId}
                  onChange={(e) => setComparisonOptionBProductId(e.target.value)}
                  className={styles.searchInput}
                >
                  <option value="">Select product</option>
                  {products.map((product) => {
                    const id = getProductId(product);
                    const name =
                      typeof product === "string"
                        ? "Loading..."
                        : capitalizeFirst(product.name || "");
                    return (
                      <option key={`b-${id}`} value={id}>
                        {name}
                      </option>
                    );
                  })}
                </select>
                <input
                  type="text"
                  value={comparisonOptionBTitle}
                  onChange={(e) => setComparisonOptionBTitle(e.target.value)}
                  placeholder="Option B title"
                  className={styles.searchInput}
                />
              </div>
            </div>
          )}

          {/* Images per Product */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>
              <FaImage /> Product Images (1-5 total) *
            </label>
            {products.length === 0 && (
              <p className={styles.helpText}>
                Select products first to upload images for each one.
              </p>
            )}
            {products.length > 0 && (
              <div className={styles.productImageList}>
                {products.map((product) => {
                  const productId = getProductId(product);
                  const existing = existingProductImages[productId] || [];
                  const added = newProductImages[productId] || [];
                  const previews = [
                    ...existing.map((img) => getImageUrl(img)),
                    ...(newProductImagePreviews[productId] || []),
                  ];
                  const totalImages = getTotalImageCount(
                    existingProductImages,
                    newProductImages,
                  );

                  return (
                    <div key={productId} className={styles.productImageSection}>
                      <div className={styles.productImageHeader}>
                        <div className={styles.productImageTitle}>
                          {typeof product === "string"
                            ? "Loading..."
                            : product.name}
                        </div>
                        <div className={styles.productImageCount}>
                          {previews.length} image
                          {previews.length !== 1 ? "s" : ""}
                        </div>
                      </div>
                      <div className={styles.imageUploadArea}>
                        {previews.length > 0 ? (
                          <div className={styles.imageGrid}>
                            {existing.map((img, index) => (
                              <div
                                key={`existing-${index}`}
                                className={styles.imagePreview}
                              >
                                <img
                                  src={getImageUrl(img)}
                                  alt={`Preview ${index + 1}`}
                                />
                                <button
                                  type="button"
                                  className={styles.removeImageButton}
                                  onClick={() =>
                                    removeExistingImage(productId, index)
                                  }
                                  aria-label="Remove selected image"
                                >
                                  <FaTimes />
                                </button>
                              </div>
                            ))}
                            {added.map((_, index) => {
                              const preview = (newProductImagePreviews[
                                productId
                              ] || [])[index];
                              return (
                                <div
                                  key={`new-${index}`}
                                  className={styles.imagePreview}
                                >
                                  <img
                                    src={preview}
                                    alt={`Preview ${index + 1}`}
                                  />
                                  <button
                                    type="button"
                                    className={styles.removeImageButton}
                                    onClick={() =>
                                      removeNewImage(productId, index)
                                    }
                                    aria-label="Remove selected image"
                                  >
                                    <FaTimes />
                                  </button>
                                </div>
                              );
                            })}
                            {totalImages < 5 && (
                              <label className={styles.addImageButton}>
                                <FaImage />
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/jpg,image/webp"
                                  multiple
                                  onChange={(e) =>
                                    handleImageSelect(productId, e)
                                  }
                                  className={styles.hiddenInput}
                                />
                              </label>
                            )}
                          </div>
                        ) : (
                          <label className={styles.uploadButton}>
                            <FaImage />
                            <span>Upload Image</span>
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/jpg,image/webp"
                              multiple
                              onChange={(e) => handleImageSelect(productId, e)}
                              className={styles.hiddenInput}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            <p className={styles.helpText}>
              Add at least one image per product. Max 5 images total.
            </p>
          </div>

          {/* Caption */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Caption (Optional)</label>
            <div className={styles.captionInputWrapper}>
              <textarea
                ref={captionInputRef}
                value={caption}
                maxLength={500}
                onChange={(e) => {
                  setCaption(e.target.value);
                  handleCaptionMentionInput(e);
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") clearCaptionMention();
                }}
                placeholder="Tell us about this post… type @ to tag a product"
                className={styles.captionInput}
                rows={4}
              />
              {captionMentionQuery !== null && (
                <MentionDropdown
                  suggestions={captionMentionSuggestions}
                  query={captionMentionQuery}
                  onSelect={(product) => insertCaptionMention(product)}
                />
              )}
            </div>
            <div className={styles.charCount}>{caption.length}/500</div>
          </div>
          {/* Visibility */}
          <div className={styles.section}>
            <label className={styles.sectionLabel}>Visibility</label>
            <div className={styles.visibilityOptions}>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="visibility"
                  value="public"
                  checked={visibility === "public"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <FaGlobe />
                <span>Public</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="visibility"
                  value="followers"
                  checked={visibility === "followers"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <FaUserFriends />
                <span>Followers</span>
              </label>
              <label className={styles.visibilityOption}>
                <input
                  type="radio"
                  name="visibility"
                  value="private"
                  checked={visibility === "private"}
                  onChange={(e) => setVisibility(e.target.value)}
                />
                <FaLock />
                <span>Private</span>
              </label>
            </div>
          </div>

          {/* Submit */}
          <div className={styles.formActions}>
            <button
              type="button"
              className={styles.cancelButton}
              disabled={loading}
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className={styles.submitButton}
              disabled={
                loading ||
                getTotalImageCount(existingProductImages, newProductImages) ===
                  0 ||
                (postType === "battle"
                  ? false
                  : products.length !== requiredProductCount) ||
                ((postType === "comparison" || postType === "battle") &&
                  (!comparisonOptionAProductId ||
                    !comparisonOptionBProductId ||
                    !comparisonOptionATitle.trim() ||
                    !comparisonOptionBTitle.trim()))
              }
            >
              {loading ? "Updating..." : "Update Post"}
            </button>
          </div>
        </form>
      </div>
    );

  return content;
};

export default EditPost;